import { useState, useEffect, useRef } from 'react'

import { useSession } from '../contexts/SessionContext'
import { usePayment } from '../contexts/PaymentContext'
import { SessionStatus } from '../constants/SessionConstants'

import {
  useDirectDebitStatus,
  DirectDebitStatus,
} from '../contexts/session/DirectDebitContext'
import SuccessScreen from '../components/pages/payments/direct-debit/SuccessScreen'
import FailedScreen from '../components/pages/payments/direct-debit/FailedScreen'
import ExpiredScreen from '../components/pages/payments/direct-debit/ExpiredScreen'
import AccountDetailsScreen from '../components/pages/payments/direct-debit/AccountDetailsScreen'
import OTPVerificationScreen from '../components/pages/payments/direct-debit/OTPVerificationScreen'
import EmailVerificationScreen from '../components/pages/payments/direct-debit/EmailVerificationScreen'
import TransferConfirmationScreen from '../components/pages/payments/direct-debit/TransferConfirmationScreen'

const DirectDebitPayment = () => {
  const { sessionId: sessionContextId, sessionData, stopSessionTimer } = useSession()

  const {
    directDebitStatus,
    userEmail,
    setUserEmail,
    userPhone,
    setUserPhone,
    userAddress,
    setUserAddress,
    accountNumber,
    setAccountNumber,
    selectedBank,
    setSelectedBank,
    accountName,
    setAccountName,
    sessionReference,
    setSessionReference,
    sessionKey,
    setSessionKey,
    sessionId,
    setSessionId,
    otpSessionId,
    setOtpSessionId,
    otpChannel,
    setOtpChannel,
    otpDestination,
    setOtpDestination,
    sessionStatus,
    setSessionStatus,
    checkoutUrl,
    setCheckoutUrl,
    showEmailVerification,
    showOTPVerification,
    showAccountDetails,
    showTransferConfirmation,
    showSuccess,
    showFailed,
    showExpired,
    setSessionKey: setDirectDebitSessionKey,
    setSessionId: setDirectDebitSessionId,
  } = useDirectDebitStatus()

  // OTP state
  const [otp, setOtp] = useState('')
  const [otpError, setOtpError] = useState('')

  useEffect(() => {
    setOtp('')
    setOtpError('')
  }, [directDebitStatus.status])

  // Sync session context values with direct debit context when using session route
  useEffect(() => {
    if (sessionContextId) {
      setDirectDebitSessionKey(sessionContextId)
      setDirectDebitSessionId(sessionContextId)
    }
  }, [sessionContextId, setDirectDebitSessionKey, setDirectDebitSessionId])

  const lastMappedStatusRef = useRef(null)
  useEffect(() => {
    if (sessionData?.status && sessionData.status !== lastMappedStatusRef.current) {
      lastMappedStatusRef.current = sessionData.status
      switch (sessionData.status) {
        case SessionStatus.INITIALIZED:
        case SessionStatus.PENDING:
          showEmailVerification()
          break
        case SessionStatus.VERIFICATION_PENDING:
          showOTPVerification()
          break
        case SessionStatus.OTP_VERIFICATION:
          showAccountDetails()
          break
        case SessionStatus.CONFIRMATION:
          // Skip if already on this screen - it was shown via initiateMandate's response,
          // which carries paymentMethodDetails; a plain session poll doesn't, so calling
          // this again here would silently blank out the account/bank details display.
          if (directDebitStatus.status !== DirectDebitStatus.TRANSFER_CONFIRMATION) {
            showTransferConfirmation(sessionData)
          }
          break
        case SessionStatus.CONFIRMED:
        case SessionStatus.SUCCESS:
          showSuccess()
          break
        case SessionStatus.FAILED:
        case SessionStatus.CANCELLED:
          showFailed(sessionData.message || '')
          break
        case SessionStatus.EXPIRED:
          stopSessionTimer()
          showExpired()
          break
        default:
          showEmailVerification()
      }
    }
  }, [sessionData])

  // Render based on status
  const renderScreen = () => {
    switch (directDebitStatus.status) {
      case DirectDebitStatus.EMAIL_VERIFICATION:
        return (
          <EmailVerificationScreen
            userEmail={userEmail}
            setUserEmail={setUserEmail}
            userPhone={userPhone}
            setUserPhone={setUserPhone}
            showOTPVerification={showOTPVerification}
            otpError={otpError}
            setOtpError={setOtpError}
            otpChannel={otpChannel}
            setOtpChannel={setOtpChannel}
            setOtpSessionId={setOtpSessionId}
            sessionKey={sessionKey}
            sessionReference={sessionReference}
            setSessionKey={setSessionKey}
            setSessionReference={setSessionReference}
          />
        )
      case DirectDebitStatus.OTP_VERIFICATION:
        return (
          <OTPVerificationScreen
            otp={otp}
            setOtp={setOtp}
            otpError={otpError}
            setOtpError={setOtpError}
            sessionKey={sessionKey}
            otpChannel={otpChannel}
            setOtpChannel={setOtpChannel}
            otpSessionId={otpSessionId}
            setOtpSessionId={setOtpSessionId}
            showEmailVerification={showEmailVerification}
            userEmail={userEmail}
            userPhone={userPhone}
            sessionReference={sessionReference}
            showAccountDetails={showAccountDetails}
          />
        )
      case DirectDebitStatus.ACCOUNT_DETAILS:
        return (
          <AccountDetailsScreen
            userAddress={userAddress}
            setUserAddress={setUserAddress}
            accountNumber={accountNumber}
            setAccountNumber={setAccountNumber}
            selectedBank={selectedBank}
            setSelectedBank={setSelectedBank}
            accountName={accountName}
            setAccountName={setAccountName}
            showOTPVerification={showOTPVerification}
            showTransferConfirmation={showTransferConfirmation}
            otpError={otpError}
            setOtpError={setOtpError}
          />
        )
      case DirectDebitStatus.TRANSFER_CONFIRMATION:
        return (
          <TransferConfirmationScreen
            sessionKey={sessionKey}
            showSuccess={showSuccess}
            otpError={otpError}
            setOtpError={setOtpError}
          />
        )
      case DirectDebitStatus.SUCCESS:
        return <SuccessScreen redirectUrl={sessionData?.redirectUri} />
      case DirectDebitStatus.FAILED:
        return <FailedScreen message={directDebitStatus.message} redirectUrl={sessionData?.redirectUri} />
      case DirectDebitStatus.EXPIRED:
        return <ExpiredScreen redirectUrl={sessionData?.redirectUri} />
      default:
        return (
          <EmailVerificationScreen
            userEmail={userEmail}
            setUserEmail={setUserEmail}
            userPhone={userPhone}
            setUserPhone={setUserPhone}
            showOTPVerification={showOTPVerification}
            otpError={otpError}
            setOtpError={setOtpError}
            otpChannel={otpChannel}
            setOtpChannel={setOtpChannel}
            setOtpSessionId={setOtpSessionId}
            sessionKey={sessionKey}
            sessionReference={sessionReference}
            setSessionKey={setSessionKey}
            setSessionReference={setSessionReference}
          />
        )
    }
  }

  return <div className='w-100'>{renderScreen()}</div>
}

export default DirectDebitPayment
